/**
 * Projects and explicit links — persistence over extension_documents
 * (extension_id 'projects'), the calendarStore shape.
 *
 * A project is a REFERENCE HOLDER: it owns nothing. Membership is a link
 * record `project:<id> —contains→ <node id>`; deleting a project tombstones the
 * project and its links, never a member. An item may be in many projects.
 *
 * WHERE A PROJECT LIVES (27.09.2026, D4): in the personal scope
 * (`personal:local` signed out, `personal:<userId>` signed in) or in a team's
 * `team:<teamId>` scope — extension 'projects', synced by the same engine task
 * that carries that team's boards and documents; no backend change. Its
 * membership links live in the SAME scope as the project. Every function takes
 * the scope explicitly (default: personal); nothing looks it up on the side.
 *
 * Team projects, and why the checks below are not optional:
 *   - a team project holds only items of THAT team (a node whose wire `scope`
 *     is the project's) — never personal/device items, another team's, or a
 *     collection (workspace-scoped, not addressable per team);
 *   - a VIEWER's push to a team scope is rejected by the server and poisons
 *     that scope's outbox forever (boardRoles.js). The UI hides the controls;
 *     this store refuses the write too, via the same `isTeamViewer` the board
 *     writers use;
 *   - a team not on the user's roster is not writable at all.
 * The user's own drawn links (relates_to, covers, …) stay personal (D3), even
 * between team items.
 *
 * Record shapes (every field enumerated — an un-enumerated field dies on save):
 *   project  { id, name, description, color, apiWorkspaces: [{ id, storage }], apiWorkspaceId, apiWorkspaceStorage (= the first), created_at, updated_at }
 *            (the API workspace binding: scope/projectBindingFields.js)
 *   link     { id, from, to, rel, created_at }
 *   repo     { id, name, remote, path, provider, host, branch, description, created_at, updated_at }
 * A link's id is DETERMINISTIC (from|rel|to) so adding the same link twice is
 * one row, and removing it needs no lookup.
 */
import { deleteRecord, listRecords, upsertRecord } from '../records/recordsClient';
import { personalScopeId } from '../services/personalScope';
import { isTeamViewer } from '../extensions/bundled/task-manager/boardsStore';
import { EXPLICIT_RELATIONS, parseNodeId } from './graphModel';
import { normalizeColor } from './projectColors';
import { graphRecordScopes, teamIdOfScope, wireScopeOf } from './graphScopes';
import { repoRefFromInput } from './repoRefs';
import { normalizeBinding } from '../scope/projectBindingFields';

export { PROJECT_COLORS, normalizeColor, projectSolidColor, randomProjectGradient } from './projectColors';

export const EXTENSION_ID = 'projects';

function uid() {
  return (
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `prj-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

export function normalizeProject(draft = {}) {
  const now = new Date().toISOString();
  return {
    id: draft.id || uid(),
    name: String(draft.name || '').trim() || 'Untitled project',
    description: String(draft.description || ''),
    color: normalizeColor(draft.color),
    ...normalizeBinding(draft),
    created_at: draft.created_at || now,
    updated_at: now,
  };
}

export function linkId(from, rel, to) {
  return `${from}|${rel}|${to}`;
}

/** Validates a link; returns the record or throws with a reason. */
export function normalizeLink({ from, to, rel }) {
  const a = parseNodeId(from);
  const b = parseNodeId(to);
  if (!a || !b) throw new Error('A link needs two valid item ids.');
  if (from === to) throw new Error('An item cannot link to itself.');
  const isMembership = rel === 'contains' && a.type === 'project';
  if (!isMembership && !EXPLICIT_RELATIONS.includes(rel)) throw new Error(`Unknown relation: ${rel}`);
  if (b.type === 'project' && isMembership) throw new Error('A project cannot contain a project.');
  return { id: linkId(from, rel, to), from, to, rel, created_at: new Date().toISOString() };
}

const ctx = (scopeKey) => ({ workspaceId: scopeKey || personalScopeId(), extensionId: EXTENSION_ID });

/** A refusal the UI can translate (`projects.error.<code>`); the message is the English fallback. */
function refusal(code, message) {
  return Object.assign(new Error(message), { code });
}

/** The engine scope key of a wire scope (`personal` → the personal bucket). */
export function scopeKeyOfWire(wire) {
  return teamIdOfScope(wire) ? String(wire) : personalScopeId();
}

function onRoster(teamId) {
  return graphRecordScopes().scopes.some((s) => s.teamId === String(teamId));
}

/**
 * May the user change records in this scope? Personal: always. A team: only a
 * member who is not a viewer. A pure answer for the UI's show/hide decisions.
 */
export function canWriteScope(scope) {
  const teamId = teamIdOfScope(scope);
  if (!teamId) return true;
  return onRoster(teamId) && !isTeamViewer(teamId);
}

/** Throws the refusal the UI shows; called before EVERY write to a scope. */
function assertWritable(scopeKey) {
  const teamId = teamIdOfScope(scopeKey);
  if (!teamId) return;
  if (!onRoster(teamId)) throw refusal('notMember', 'You are not a member of this team.');
  if (isTeamViewer(teamId)) throw refusal('viewer', 'Viewers cannot change team projects.');
}

const projectKey = (project) => (typeof project === 'string' ? personalScopeId() : project?.scopeKey || personalScopeId());
const projectIdOf = (project) => String(typeof project === 'string' ? project : project?.id);

/**
 * Can `node` (a graph node) join `project`? Personal projects take anything
 * but a project; a team project only items whose wire scope is its own team,
 * and only for a user who may write that team.
 */
export function projectAccepts(project, node) {
  if (!node || node.type === 'project') return false;
  const key = projectKey(project);
  if (!canWriteScope(key)) return false;
  return !teamIdOfScope(key) || node.scope === wireScopeOf(key);
}

const rowData = (row) => {
  const { record_type, parent_id, sort_key, ...data } = row || {};
  return data;
};

async function list(recordType, scopeKey) {
  const rows = await listRecords({ ...ctx(scopeKey), recordType, scopeId: null });
  return (rows || []).map(rowData);
}

export async function listProjects(scopeKey) {
  const key = scopeKey || personalScopeId();
  return (await list('project', key))
    .filter((p) => p.id)
    .map((p) => ({ ...normalizeProject(p), updated_at: p.updated_at, scopeKey: key }));
}

export async function listLinks(scopeKey) {
  const key = scopeKey || personalScopeId();
  return (await list('link', key)).filter((l) => l.id && l.from && l.to && l.rel).map((l) => ({ ...l, scopeKey: key }));
}

export async function saveProject(draft, scopeKey) {
  const key = scopeKey || personalScopeId();
  assertWritable(key);
  const project = normalizeProject(draft);
  await upsertRecord({
    ...ctx(key),
    record: { id: project.id, record_type: 'project', parent_id: null, sort_key: null, data: project },
  });
  return { ...project, scopeKey: key };
}

/** A link the user drew (relates_to, covers, …): personal, always (D3). */
export async function addLink(input) {
  return writeLink(input, personalScopeId());
}

async function writeLink(input, scopeKey) {
  assertWritable(scopeKey);
  const link = normalizeLink(input);
  await upsertRecord({
    ...ctx(scopeKey),
    record: { id: link.id, record_type: 'link', parent_id: null, sort_key: null, data: link },
  });
  return link;
}

export async function removeLink(id, scopeKey) {
  const key = scopeKey || personalScopeId();
  assertWritable(key);
  await deleteRecord({ ...ctx(key), recordId: String(id) });
}

export function membershipLink(projectId, memberNodeId) {
  return { from: `project:${projectId}`, to: memberNodeId, rel: 'contains' };
}

/**
 * @param project  `{ id, scopeKey }` (a bare id means a personal project)
 * @param member   the graph node `{ id, scope }` (a bare node id is accepted
 *                 for a personal project only — a team project must see the
 *                 member's scope to check it)
 */
export async function addToProject(project, member) {
  const key = projectKey(project);
  const memberId = typeof member === 'string' ? member : member?.id;
  if (teamIdOfScope(key)) {
    assertWritable(key);
    const memberScope = typeof member === 'string' ? undefined : member?.scope;
    if (memberScope !== wireScopeOf(key)) throw refusal('teamOnly', 'A team project can only hold items of that team.');
  }
  return writeLink(membershipLink(projectIdOf(project), memberId), key);
}

export async function removeFromProject(project, memberNodeId) {
  return removeLink(linkId(`project:${projectIdOf(project)}`, 'contains', memberNodeId), projectKey(project));
}

/**
 * Deletes the project and every link in ITS scope that starts or ends at it.
 * Members are untouched — that is the whole point of a reference holder.
 */
export async function deleteProject(project) {
  const key = projectKey(project);
  const id = projectIdOf(project);
  assertWritable(key);
  const node = `project:${id}`;
  // Always read fresh: a caller's snapshot can miss a link added or synced a
  // moment ago, which would survive as an orphan record (review).
  const all = await listLinks(key);
  for (const l of all) {
    // eslint-disable-next-line no-await-in-loop
    if (l.from === node || l.to === node) await removeLink(l.id, key);
  }
  await deleteRecord({ ...ctx(key), recordId: id });
  const repos = all.filter((l) => l.from === node && l.rel === 'contains' && l.to.startsWith('git_repo:'));
  for (const l of repos) {
    // eslint-disable-next-line no-await-in-loop
    await dropRepoIfOrphan(l.to.slice('git_repo:'.length), key);
  }
}

// ── code repositories (card 5c626304) ───────────────────────────────────────
// A repo is a REFERENCE record in the project's scope plus a membership link
// `project:<id> —contains→ git_repo:<id>`. Unlinked from its last project in
// that scope, the record goes too: a repo is only ever shown through a project.

export function normalizeRepo(draft = {}) {
  const now = new Date().toISOString();
  const ref = repoRefFromInput({ url: draft.remote, path: draft.path, name: draft.name, branch: draft.branch, description: draft.description });
  return { id: draft.id || uid(), ...ref, created_at: draft.created_at || now, updated_at: now };
}

export async function listRepos(scopeKey) {
  const key = scopeKey || personalScopeId();
  const rows = await list('repo', key);
  const out = [];
  for (const r of rows) {
    if (!r.id) continue;
    try {
      out.push({ ...normalizeRepo(r), updated_at: r.updated_at, scopeKey: key });
    } catch {
      // A synced row that is no valid reference (a password in its URL, no
      // place at all) is not shown — never repaired into something else.
    }
  }
  return out;
}

/**
 * The repo rows `listRepos` hides (a token or password in the saved URL, or
 * no place at all), so the project page can show them as "needs fixing" with
 * a Remove button instead of leaving a link nobody can see or unlink (owner
 * decision 05.10.2026 — an IDE shows an invalid VCS root and offers to remove
 * it, it does not hide it). Only the id and the HOST: the URL itself, with
 * its token, never leaves this function.
 *
 * @returns {Promise<{ id: string, name: string, host: string|null }[]>}
 */
export async function listBrokenRepos(scopeKey) {
  const key = scopeKey || personalScopeId();
  const rows = await list('repo', key);
  const out = [];
  for (const r of rows) {
    if (!r.id) continue;
    try {
      normalizeRepo(r);
    } catch {
      const remote = String(r.remote || '');
      let host = null;
      try {
        host = new URL(remote).hostname || null;
      } catch {
        host = /^[^@\s/]+@([^:/\s]+):/.exec(remote)?.[1] || null; // git@host:owner/repo
      }
      out.push({ id: String(r.id), name: String(r.name || '').slice(0, 120), host });
    }
  }
  return out;
}

/**
 * Creates (or edits, when `draft.id` is set) a repository and links it to the
 * project. A team project needs a remote: a local folder means nothing on a
 * teammate's machine and its path names this user's account folders.
 */
export async function linkRepoToProject(project, draft) {
  const key = projectKey(project);
  const repo = await saveRepo(draft, key);
  await writeLink(membershipLink(projectIdOf(project), `git_repo:${repo.id}`), key);
  return { ...repo, scopeKey: key };
}

/** Writes the repo record alone (git sync applies a pulled one through this). */
export async function saveRepo(draft, scopeKey) {
  const key = scopeKey || personalScopeId();
  assertWritable(key);
  const repo = normalizeRepo(draft);
  if (teamIdOfScope(key) && !repo.remote) throw refusal('teamRemoteOnly', 'A team project can only link a repository by its URL.');
  await upsertRecord({
    ...ctx(key),
    record: { id: repo.id, record_type: 'repo', parent_id: null, sort_key: null, data: repo },
  });
  return repo;
}

/** Removes the repo from the project; the record goes when no project in its scope holds it any more. */
export async function unlinkRepoFromProject(project, repoId) {
  const key = projectKey(project);
  assertWritable(key);
  const node = `git_repo:${repoId}`;
  await removeLink(linkId(`project:${projectIdOf(project)}`, 'contains', node), key);
  await dropRepoIfOrphan(repoId, key);
}

/** Deletes the repo record when no project in its scope links it; true when it did. */
export async function dropRepoIfOrphan(repoId, key) {
  const node = `git_repo:${repoId}`;
  const held = (await listLinks(key)).some((l) => l.rel === 'contains' && l.to === node);
  if (held) return false;
  await deleteRecord({ ...ctx(key), recordId: String(repoId) });
  return true;
}
